import { Box, Typography } from "@mui/material";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Tooltip as ReactToolTip } from "react-tooltip";
import DuomenysKraunami from "../shared/DuomenysKraunami";
import {
  bendrosSpalvosProduktyvumoLinijaiIrStulpeliams,
  Spalvos,
  SpalvosProduktyvumoLinijai,
  SpalvosProduktyvumoStulpeliams,
  X11colors,
} from "../../constants/Spalvos";
import { useEffect, useState } from "react";
import { isAllDataNull } from "../../functions/produktyvumas/grafikuReziai";
import DuomenuNera from "../shared/DuomenuNera";

const CustomLegend = ({ payload }) => {
  const [touchedItem, setTouchedItem] = useState(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice(window.matchMedia("(hover: none)").matches);
    };

    checkTouchDevice();
    window.addEventListener("resize", checkTouchDevice);

    return () => window.removeEventListener("resize", checkTouchDevice);
  }, []);

  useEffect(() => {
    if (isTouchDevice && touchedItem !== null) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 5000);

      return () => {
        clearTimeout(timer);
        setIsVisible(true);
      };
    }
  }, [isTouchDevice, touchedItem]);

  return (
    <Box>
      <Box
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        {payload.map((entry, index) => (
          <Box
            key={index}
            data-tooltip-id="legend-tooltip"
            data-tooltip-content={entry.value}
            style={{
              display: "flex",
              alignItems: "center",
              margin: "0 8px",
              overflow: "hidden",
              ...(isTouchDevice && { touchAction: "manipulation" }),
            }}
            onClick={() => {
              if (isTouchDevice) {
                setTouchedItem(touchedItem === index ? null : index);
              }
            }}
          >
            <Box
              style={{
                width: 12,
                height: 12,
                backgroundColor: entry.color,
                borderRadius: 3,
                marginRight: 5,
              }}
            />
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {entry.value.length > 60 ? `${entry.value.substring(0, 60)}...` : entry.value}{" "}
            </span>
            {isVisible && isTouchDevice && touchedItem === index && (
              <Box
                style={{
                  position: "absolute",
                  top: "-40px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "#fff",
                  padding: "5px 10px",
                  borderRadius: "5px",
                  boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
                  whiteSpace: "normal",
                  wordBreak: "normal",
                  textAlign: "center",
                  zIndex: 9999,
                }}
              >
                {entry.value}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* Single tooltip for all legend items */}
      <ReactToolTip
        id="legend-tooltip"
        place="top"
        effect="solid"
        delayShow={300}
        style={{
          zIndex: 9999,
          pointerEvents: "none",
        }}
        float={true}
      />
    </Box>
  );
};

const DvigubasGrafikas = ({
  pavadinimas,
  isLoading,
  chartDataLine,
  yAxisMinLine,
  yAxisMaxLine,
  raktaiLine,
  chartDataBar,
  yAxisMinBar,
  yAxisMaxBar,
  raktaiBar,
  barHeight = 400,
  xAsis,
  asisKaireje = null,
  asisDesineje = null,
  asisApacioje = null,
  kitokiaLegenda = true,
  etikete = false,
  papildomasResponsive,
  barSize = 12,
  position = "center",
  angle = 0,
  fill = "white",
  prevLacDataIsHigherThanCurrentOne = false,
}) => {
  const [minBarHeight, setMinBarHeight] = useState(350);
  const [reikiaEtiketes, setReikiaEtiketes] = useState(true);

  useEffect(() => {
    const updateLegendPosition = () => {
      if (window.innerWidth < 1400 || (papildomasResponsive && window.innerWidth < 1650)) {
        setReikiaEtiketes(false);
      } else {
        setReikiaEtiketes(true);
      }
    };

    updateLegendPosition(); // pradine busena
    window.addEventListener("resize", updateLegendPosition);

    return () => window.removeEventListener("resize", updateLegendPosition);
  }, []);

  useEffect(() => {
    const updateLegendPosition = () => {
      if (window.innerWidth < 800) {
        setMinBarHeight(barHeight ? barHeight : 300);
      } else {
        setMinBarHeight(350);
      }
    };

    updateLegendPosition();
    window.addEventListener("resize", updateLegendPosition);

    return () => window.removeEventListener("resize", updateLegendPosition);
  }, []);

  if (isLoading) return <DuomenysKraunami pavadinimas={pavadinimas} />;

  const isBarDataAvailable = isAllDataNull(chartDataBar, xAsis);
  const isLineDataAvailable = isAllDataNull(chartDataLine, xAsis);

  if (isBarDataAvailable && isLineDataAvailable) {
    return <DuomenuNera pavadinimas={pavadinimas} />;
  }

  // sujungia masyvas pagal menesius
  const mergedData = chartDataLine.map((linItem) => {
    const matchingStulpelisItem = chartDataBar.find(
      (stulpItem) => stulpItem[xAsis] === linItem[xAsis]
    );

    return {
      ...linItem,
      ...(matchingStulpelisItem || {}),
    };
  });

  const mergedKeys = [...raktaiLine, ...raktaiBar];

  return (
    <>
      <Typography>{pavadinimas}</Typography>
      <ResponsiveContainer width="100%" height="100%" minHeight={minBarHeight}>
        <ComposedChart data={mergedData} width="100%" height="100%" minHeight={minBarHeight}>
          <XAxis dataKey={xAsis} style={{ fontSize: 16 }}>
            {asisApacioje && (
              <Label
                value={asisApacioje}
                position="insideBottom"
                style={{ textAnchor: "middle", fill: "#666", fontSize: 14 }}
                offset={-2}
                overflow={"visible"}
              />
            )}
          </XAxis>
          <YAxis
            yAxisId="left"
            orientation="left"
            style={{ fontSize: 16 }}
            domain={[yAxisMinLine, yAxisMaxLine]}
          >
            {asisDesineje && (
              <Label
                value={asisDesineje}
                angle={-90}
                position="insideRight"
                style={{ textAnchor: "middle", fill: "#666", fontSize: 14 }}
                offset={60}
                overflow={"visible"}
              />
            )}
          </YAxis>
          <YAxis
            yAxisId="right"
            orientation="right"
            style={{ fontSize: 16 }}
            domain={[yAxisMinBar, yAxisMaxBar]}
          >
            {asisKaireje && (
              <Label
                value={asisKaireje}
                angle={-90}
                position="insideLeft"
                style={{ textAnchor: "middle", fill: "#666", fontSize: 14 }}
                offset={50}
              />
            )}
          </YAxis>
          <Tooltip />
          {kitokiaLegenda ? (
            <Legend
              align="center"
              content={(props) => (
                <CustomLegend
                  {...props}
                  payload={[
                    ...mergedKeys.map((k, index) => ({
                      value: k,
                      type: "line",
                      id: k,
                      color:
                        mergedKeys.length >= 5
                          ? Spalvos[index]
                          : bendrosSpalvosProduktyvumoLinijaiIrStulpeliams[index],
                    })),
                  ]}
                />
              )}
              verticalAlign="top"
              wrapperStyle={{ paddingBottom: 20 }}
              formatter={(value, entry) => <span style={{ color: entry.color }}>{value}</span>}
            />
          ) : (
            <Legend
              align="center"
              verticalAlign="top"
              wrapperStyle={{ paddingBottom: 20 }}
              formatter={(value, entry) => <span style={{ color: entry.color }}>{value}</span>}
            />
          )}
          <CartesianGrid stroke="#f5f5f5" />

          {raktaiBar.map((k, index) => (
            <Bar
              barSize={barSize}
              key={k}
              yAxisId="right"
              dataKey={k}
              fill={
                raktaiBar.length >= 5 ? X11colors[index] : SpalvosProduktyvumoStulpeliams[index]
              }
            >
              {etikete && reikiaEtiketes && (
                <LabelList datakey="k" position={position} angle={angle} offset={10} fill={fill} />
              )}
            </Bar>
          ))}
          {raktaiLine.map((k, index) => (
            <Line
              key={k}
              yAxisId="left"
              dataKey={k}
              type="monotone"
              strokeWidth="8px"
              stroke={raktaiLine.length >= 5 ? Spalvos[index] : SpalvosProduktyvumoLinijai[index]}
            >
              {etikete && reikiaEtiketes && (
                <LabelList
                  datakey={k}
                  position={
                    k === "Vid. pieno per buvusias laktacijas" && prevLacDataIsHigherThanCurrentOne
                      ? "top"
                      : k === "Vid. pieno per buvusias laktacijas"
                      ? "bottom"
                      : k === "Vid. pieno per einamąją laktaciją" &&
                        prevLacDataIsHigherThanCurrentOne
                      ? "bottom"
                      : "top"
                  }
                  offset={10}
                  fill={raktaiLine.length >= 5 ? Spalvos[index] : SpalvosProduktyvumoLinijai[index]}
                />
              )}
            </Line>
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </>
  );
};

export default DvigubasGrafikas;
